import React, { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { ActionHandlerFunction, OSInfo } from "@preveil-api";
import { AdminUIActionTypes, AdminErrorMessages, useAppSelector, SupportedPlatform, DRIVE_INVALID_CHARACTERS } from "src/common";
import { RootState } from "src/store/configureStore";

type AllProps = {
  show: boolean;
  handleAction: ActionHandlerFunction;
}

// Description: This component is used to render the modal to get the location to export the data to 
function GetLocationModalComponent(props: AllProps) {
  const { show, handleAction } = props;
  const os_info = useAppSelector((state: RootState) => state.app.os_info);
  const [drop_dir, setDropDir] = useState("");

  useEffect(() => {
    // Making sure the state of drop_dir is empty when the modal is closed
    if (!show && !!drop_dir) {
      setDropDir("");
    }
  }, [show]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.trim();
    setDropDir(value);
  };

  // Description: We need to verify the export path before starting the export: no illegal characters or spaces are allowed in the path.
  // If the path is invalid, return null.
  function preVerifyStartExport(file_location: string, os_info?: OSInfo | null | undefined) {
    let new_folder: string;
    if (os_info?.user_platform === SupportedPlatform.WIN) {
      new_folder = file_location.replace(/.*\\/, "");
    } else {
      new_folder = file_location.replace(/.*\//, "");
    }

    if (/\s/.test(new_folder) || DRIVE_INVALID_CHARACTERS.find(exception => new_folder.includes(exception))) {
      return null;
    }

    return file_location.toString().trim();
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();
    const file_location = preVerifyStartExport(drop_dir, os_info);
    if (!file_location) {
      const message = AdminErrorMessages.error_data_export_illegal_file;
      const stack = drop_dir;
      handleAction({ actionType: AdminUIActionTypes.PageError, params: { message, stack } });
    } else {
      handleAction({ actionType: AdminUIActionTypes.StartDataExport, params: drop_dir });
    }
  };

  function handleCancel() {
    setDropDir("");
    handleAction({ actionType: AdminUIActionTypes.ShowModal });
  }

  return <Modal
    size="lg"
    show={show}
    onHide={() => handleAction({ actionType: AdminUIActionTypes.ShowModal })}
    centered
  >
    <Modal.Header closeButton>
      <Modal.Title>Choose Location</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>Enter a file path to any folder to which you have write permissions. The file path needs to end with a new folder that will be created to hold the data. Your exported data will be copied to that location.</p>
      <p>(eg): {os_info?.data_export_example_path} </p>
      <Form noValidate onSubmit={handleSubmit}>
        <Form.Group>
          <Form.Label htmlFor="path_tb">File Path</Form.Label>
          <Form.Control
            id="path_tb"
            value={drop_dir}
            onChange={handleChange}
            type="text"
            placeholder="Enter file path"
            required
          />
        </Form.Group>
        <div className="btn-panel mt-3">
          <Button
            type="submit"
            variant="primary"
            disabled={!drop_dir}
          >
            Start Export
          </Button>
          <Button variant="no-outline-primary" onClick={handleCancel}>Cancel</Button>
        </div>
      </Form>
    </Modal.Body>
  </Modal>;
}

export default React.memo(GetLocationModalComponent);